import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import httpStatus from 'http-status'

import { Controller, useForm } from 'react-hook-form'

import { View } from '../../atoms/View'

import { GenericErrorBox } from '../../molecules/GenericErrorBox'
import GenericWarningBox from '../../molecules/GenericWarningBox'

import { PasswordLoginTemplate } from '../../templates/PasswordLoginTemplate'

import Lurker from '../../../util/lurker'
import Tracker from '../../../util/tracker'
import behaviors from '../../../util/behaviors'
import { Turnstile } from '../../molecules/Turnstile'
import { getModalLoginExtraData } from '../../../util/experiments'
import storage from '../../../util/storage'
import {
  LOGIN_METHOD_KEY,
  MANDATORY_TIPIFICATION_KEY,
  PASSWORD_LOGIN_METHOD,
} from '../../../util/global.consts'
import { LURKER_ACCOUNT_STATUS } from '../SplitLoginEmailPage/SplitLoginEmailPage.consts'

const pageName = 'login_password'
const lurker = new Lurker(pageName)
const tracker = new Tracker(pageName)

const ErrorMsgWrapper = styled(View)`
  margin-top: 16px;
  margin-right: 0;
  margin-bottom: 16px;
  margin-left: 0;
`

const InstabilityBox = styled(GenericWarningBox)`
  border-style: none;
  border-radius: 8px;
  margin: 15px 0px 10px 0px;
  padding: 16px 24px;
  background: #fff7e0;
`
const InstabilityBoxFade = styled(View)`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  div {
    animation-name: fadeIn;
    animation-duration: 1s;
  }
`

const apiErrors = {
  Unauthorized: 'showUnauthorizedError',
  Inactive: 'showInactiveError',
  Generic: 'showGenericError',
  RateLimit: 'showRateLimitError',
  AccountNotFound: 'showAccountNotFoundError',
  InvalidCredentials: 'showInvalidCredentialsError',
  GoogleInitialization: 'showGoogleInitializationError',
  ExpiredToken: 'showExpiredTokenError',
  RedScore: 'showRedScoreError',
  AccountLocked: 'showAccountLockedError',
  AccountDisabledByFraud: 'showAccountDisabledByFraudError',
  Instability: 'showInstabilityError',
  NoNetwork: 'showNetworkError',
  MaxPasswordAttempts: 'showPasswordlessError',
  PasswordMismatch: 'showPasswordMismatchError',
}

const MAX_PASSWORD_ATTEMPTS = 3

export const SplitLoginPasswordPage = ({
  email,
  onChallengePinCodeCall,
  onError,
  onForgotPassword,
  onMaxPasswordAttemptsReached,
  onSubmit,
  onLoginSuccess,
  onPhoneNotValidatedError,
  onBackClick,
  onAccountNotValidated,
  onAccountMustValidate,
  passwordlessSignIn,
  abTests = '',
  sessionTokens,
  modalMode,
  onAccountCompleteData,
  turnstileEnabled,
}) => {
  const [currentError, setCurrentError] = useState('')
  const [passwordAttempts, setPasswordAttempts] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [turnstileReady, setTurnstileReady] = useState(false)

  const {
    control,
    formState: { errors },
    setValue,
    register,
    handleSubmit,
  } = useForm({
    defaultValues: { password: '', turnstileToken: '' },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const onPasswordSubmit = ({ password, turnstileToken }) => {
    const extraData = getModalLoginExtraData(abTests, modalMode)
    lurker.clickEvt('login-button', extraData)
    setIsSubmitting(true)

    onSubmit(password, turnstileToken, sessionTokens)
      .then(data => {
        const { publicAccountId } = data
        lurker.setPublicAccountId(publicAccountId)
        const method = 'email_and_password'
        lurker.viewMessageEvt('email_login_success', {
          accounts_method: method,
          remote_addr: data.remoteAddress,
          accountId: data.accountId,
          ...extraData,
        })
        onLoginSuccess && onLoginSuccess({ ...data, method: PASSWORD_LOGIN_METHOD })
      })
      .catch(onPasswordLoginError)
      .finally(() => setIsSubmitting(false))
  }

  const getErrorAlertbox = error => {
    const errorMessages = {
      [apiErrors.Instability]: (
        <InstabilityBoxFade>
          <InstabilityBox>
            <span className="typo-body-medium text-feedback-attention-110">
              Podemos apresentar instabilidades no login neste momento. Já estamos trabalhando para
              resolvê-las!
            </span>
          </InstabilityBox>
        </InstabilityBoxFade>
      ),
      [apiErrors.Generic]: (
        <ErrorMsgWrapper>
          <GenericErrorBox text="Não foi possível acessar sua conta. Por favor, tente novamente." />
        </ErrorMsgWrapper>
      ),
      [apiErrors.GoogleInitialization]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showGoogleInitializationError
            text="Não foi possível entrar com o Google."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.PasswordMismatch]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showPasswordMismatchError
            errorAction={onForgotPasswordClick}
            text="Sua senha está incorreta."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.NoNetwork]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showContactUs={false}
            text="Não foi possível acessar sua conta."
            info="Por favor, verifique sua conexão com a internet e tente novamente."
          />
        </ErrorMsgWrapper>
      ),
      [apiErrors.InvalidCredentials]: (
        <ErrorMsgWrapper>
          <GenericErrorBox
            showContactUs={false}
            text="Email e/ou senha inválidos, verifique seus dados e tente novamente."
          />
        </ErrorMsgWrapper>
      ),
    }

    return errorMessages[error] || errorMessages[apiErrors.Generic]
  }

  const onUnauthorizedError = (response, isSmartLock) => {
    const prefix = isSmartLock ? 'smart_lock_' : ''
    const { message = 'UNAUTHORIZED' } = response.data || {}
    const extraData = getModalLoginExtraData(abTests, modalMode)

    const unauthorizedActions = {
      PIN_CODE: () => {
        if (typeof onChallengePinCodeCall === 'function') {
          lurker.viewMessageEvt('error', {
            reason: `${prefix}${message.toLowerCase()}`,
            accounts_status_code: response.status,
            type: response.data.type,
            ...extraData,
          })
          storage.set(LOGIN_METHOD_KEY, PASSWORD_LOGIN_METHOD)
          onChallengePinCodeCall(response.data)
        }
      },
      ACCOUNT_NOT_FOUND: () => {
        setCurrentError(apiErrors.AccountNotFound)
        lurker.viewMessageEvt('error', {
          reason: `${prefix}${message.toLowerCase()}`,
          accounts_status_code: response.status,
          status: LURKER_ACCOUNT_STATUS.ACCOUNT_NOT_FOUND,
          ...extraData,
        })
      },
      INVALID_CREDENTIALS: () => {
        setCurrentError(apiErrors.InvalidCredentials)
        lurker.viewMessageEvt('error', {
          reason: `${prefix}invalid_credentials`,
          accounts_status_code: response.status,
          ...extraData,
        })
      },
      PASSWORD_MISMATCH: () => {
        lurker.viewMessageEvt('error', {
          reason: `${prefix}${message.toLowerCase()}`,
          accounts_status_code: response.status,
          ...extraData,
        })
        onPasswordMismatchError()
      },
      PHONE_NOT_VALIDATED: () => {
        if (typeof onPhoneNotValidatedError === 'function') {
          lurker.redirectEvt('phone_validation')
          storage.set(LOGIN_METHOD_KEY, PASSWORD_LOGIN_METHOD)
          onPhoneNotValidatedError(response.data.token)
        }
      },
      ACCOUNT_NOT_VALIDATED: () => {
        onAccountNotValidated(response?.data?.token)

        lurker.viewMessageEvt('error', {
          reason: `${prefix}account_not_validated`,
          accounts_status_code: response.status,
          ...extraData,
        })
      },
      ACCOUNT_TYPING_REQUIRED: () => {
        lurker.viewMessageEvt('error', {
          accounts_reason: 'ACCOUNT_TYPING_REQUIRED'.toLowerCase(),
          accounts_status_code: response.status,
          login_type: 'password',
        })

        storage.set(MANDATORY_TIPIFICATION_KEY, true)

        if (onAccountCompleteData) {
          onAccountCompleteData(response?.data?.token)
        }
      },
      ACCOUNT_MUST_VALIDATE: () => {
        lurker.viewMessageEvt('error', {
          reason: `${prefix}${message.toLowerCase()}`,
          accounts_status_code: response.status,
          ...extraData,
        })
        onAccountMustValidate(response?.data?.token)
      },
      default: () => {
        lurker.viewMessageEvt('error', {
          reason: `${prefix}${message.toLowerCase()}`,
          accounts_status_code: response.status,
          ...extraData,
        })
        setCurrentError(apiErrors.Unauthorized)
      },
    }
    unauthorizedActions[message] ? unauthorizedActions[message]() : unauthorizedActions.default()
  }

  const onPasswordMismatchError = () => {
    if (passwordAttempts >= MAX_PASSWORD_ATTEMPTS) {
      onMaxPasswordAttemptsReached()
        .then(() => {
          setCurrentError(apiErrors.MaxPasswordAttempts)
        })
        .catch(() => setCurrentError(apiErrors.PasswordMismatch))
    } else {
      setCurrentError(apiErrors.PasswordMismatch)
      if (passwordlessSignIn) {
        setPasswordAttempts(passwordAttempts => passwordAttempts + 1)
      }
    }
  }

  const handleUnexpectedError = (error, prefix) => {
    if (error?.message?.includes('timeout')) {
      setCurrentError(apiErrors.NoNetwork)
    }

    const extraData = getModalLoginExtraData(abTests, modalMode)

    if (error?.response?.status) {
      lurker.viewMessageEvt('error', {
        reason: `${prefix}unexpected_status_code`,
        accounts_status_code: error?.response.status,
        ...extraData,
      })
      setCurrentError(apiErrors.Generic)
    } else {
      lurker.viewMessageEvt('error', {
        reason: `${prefix}unexpected_error`,
        accounts_error_message: error?.message || 'unknown',
        ...extraData,
      })
      setCurrentError(apiErrors.Generic)
    }
  }

  const onApiError = (error = {}, isSmartLock) => {
    const { response = {} } = error

    const prefix = isSmartLock ? 'smart_lock_' : ''
    const extraData = getModalLoginExtraData(abTests, modalMode)

    const errorActions = {
      'Network Error': () => {
        lurker.viewMessageEvt('login_timeout')
        setCurrentError(apiErrors.NoNetwork)
      },
      [httpStatus.UNAUTHORIZED]: () => {
        onUnauthorizedError(response, isSmartLock)
      },
      [httpStatus.LOCKED]: () => {
        lurker.viewMessageEvt('error', {
          reason: `${prefix}account_locked`,
          accounts_status_code: response.status,
          status: LURKER_ACCOUNT_STATUS.ACCOUNT_NOT_FOUND,
          ...extraData,
        })
        setCurrentError(apiErrors.AccountLocked)
      },
      [httpStatus.TOO_MANY_REQUESTS]: () => {
        lurker.viewMessageEvt('error', {
          reason: 'rate_limit_exceeded',
          accounts_status_code: response.status,
          ...extraData,
        })
        setCurrentError(apiErrors.RateLimit)
      },
      [httpStatus.GONE]: () => {
        lurker.viewMessageEvt('error', {
          reason: `${prefix}account_inactive`,
          accounts_status_code: response.status,
          status: LURKER_ACCOUNT_STATUS.ACCOUNT_INACTIVE,
          ...extraData,
        })
        setCurrentError(apiErrors.Inactive)
      },
      [httpStatus.FORBIDDEN]: () => {
        const { message = 'TURNSTILE_INVALID_TOKEN' } = response.data || {}

        switch (message) {
          case 'ACCOUNT_DISABLED':
            lurker.viewMessageEvt('error', {
              reason: `${prefix}${message.toLowerCase()}`,
              accounts_status_code: response.status,
              status: LURKER_ACCOUNT_STATUS.ACCOUNT_ON_RED_ZONE,
              ...extraData,
            })
            setCurrentError(apiErrors.AccountDisabledByFraud)
            break
          default:
            lurker.viewMessageEvt('error', {
              reason: `${prefix}${message.toLowerCase()}`,
              accounts_status_code: response.status,
              ...extraData,
            })
            setCurrentError(apiErrors.Generic)
        }
      },
      [httpStatus.BAD_REQUEST]: () => {
        if (response.data && response.data.trim() === 'Missing turnstile token') {
          lurker.viewMessageEvt('error', {
            reason: `${prefix}turnstile_missing_token`,
            accounts_status_code: response.status,
            ...extraData,
          })
          setCurrentError(apiErrors.Generic)
        }
      },
      default: () => {
        handleUnexpectedError(response)
      },
    }

    const action =
      errorActions[response.status] || errorActions[error?.message] || errorActions.default
    return action()
  }

  const onPasswordLoginError = (error = {}, isSmartLock) => {
    if (onError) {
      onError()
    }

    window?.turnstile?.reset()

    onApiError(error, isSmartLock)
    behaviors.scrollToTop()
  }

  const onTurnstileVerify = turnstileToken => {
    setTurnstileReady(true)
    setValue('turnstileToken', turnstileToken, { shouldValidate: true })
  }

  const shouldDisableSubmit = () => {
    return isSubmitting || (turnstileEnabled && !turnstileReady)
  }

  useEffect(() => {
    lurker.pageLoadEvt()
    tracker.pageLoadEvt()
  }, [])

  const onForgotPasswordClick = () => {
    lurker.clickEvt('forgot_password')

    if (onForgotPassword) {
      onForgotPassword()
    }
  }

  const onBackLinkClickVariant = () => {
    lurker.clickEvt('back')

    if (onBackClick) {
      onBackClick()
    }
  }

  return (
    <PasswordLoginTemplate
      email={email}
      abTests={abTests}
      onSubmit={handleSubmit(onPasswordSubmit)}
      onForgotPassword={onForgotPasswordClick}
      onBackClick={onBackLinkClickVariant}
      isSubmitting={isSubmitting}
      shouldDisableSubmit={shouldDisableSubmit()}
      errorMsg={currentError ? getErrorAlertbox(currentError) : null}
      lurker={lurker}
      captchaChallenge={
        turnstileEnabled ? (
          <Turnstile
            {...register('turnstileToken', { required: true })}
            onVerify={onTurnstileVerify}
            hasError={!!errors.turnstileToken}
          />
        ) : null
      }
      inputWrapper={renderComponent => {
        return (
          <Controller
            control={control}
            name="password"
            rules={{
              required: 'Campo obrigatório. Informe sua senha.',
            }}
            render={renderComponent}
          />
        )
      }}
      modalMode={modalMode}
    />
  )
}

SplitLoginPasswordPage.propTypes = {
  abTests: PropTypes.string,
  email: PropTypes.string,
  onChallengePinCodeCall: PropTypes.func,
  onError: PropTypes.func,
  onForgotPassword: PropTypes.func,
  onLoginSuccess: PropTypes.func,
  onMagicLinkEmailSentClick: PropTypes.func,
  onBackClick: PropTypes.func,
  onPhoneNotValidatedError: PropTypes.func,
  onMaxPasswordAttemptsReached: PropTypes.func,
  onAccountNotValidated: PropTypes.func,
  onAccountMustValidate: PropTypes.func,
  onSubmit: PropTypes.func,
  showReactivationLink: PropTypes.bool,
  passwordlessSignIn: PropTypes.bool,
  smartLockLogin: PropTypes.bool,
  sessionTokens: PropTypes.object,
  modalMode: PropTypes.bool,
  onAccountCompleteData: PropTypes.func,
  turnstileEnabled: PropTypes.bool,
}
